<template>
  <v-container fluid class="fill-height" id="container">
    <v-layout justify-center style="margin-top: -120px;">
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4" xl="3">
          <v-card flat color="transparent" dark>
            <v-card-text class="text-center">
              <h1 class="display-1">
                <strong>{{ appName }}</strong>
              </h1>
              <p class="subheading mb-2">
                {{ $t("login.login_to_account") }}
              </p>
            </v-card-text>
          </v-card>
          <v-card class="elevation-12" flat>
            <v-toolbar color="primary" dark flat>
              <v-toolbar-title>{{ $t("login.connect") }}</v-toolbar-title>
              <v-spacer />
              <v-tooltip bottom v-if="basiAuth">
                <template v-slot:activator="{ on }">
                  <v-btn icon large v-on="on" @click="basiAuth = false">
                    <v-icon>vpn_key</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("login.user_token") }}</span>
              </v-tooltip>
              <v-tooltip right v-else>
                <template v-slot:activator="{ on }">
                  <v-btn icon large v-on="on" @click="basiAuth = true">
                    <v-icon>account_circle</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("login.basic_auth") }}</span>
              </v-tooltip>
            </v-toolbar>

            <v-card-text class="px-8 py-8">
              <v-alert type="error" v-if="errorMsg">
                {{ errorMsg }}
              </v-alert>
              <form @submit.prevent="onLogin">
                <v-row>
                  <v-col cols="12" xs="12">
                    <v-text-field
                      v-if="basiAuth"
                      prepend-inner-icon="person"
                      outlined
                      color="blue darken-1"
                      name="username"
                      label="Username"
                      type="text"
                      ref="username"
                      v-model="form.username"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12">
                    <v-text-field
                      v-if="!basiAuth"
                      prepend-inner-icon="person"
                      outlined
                      color="blue darken-1"
                      name="userToken"
                      label="User Token"
                      type="text"
                      ref="user_token"
                      v-model="form.userToken"
                      required
                    ></v-text-field>
                  </v-col>
                  <v-col cols="12" xs="12">
                    <v-text-field
                      v-if="basiAuth"
                      prepend-inner-icon="lock"
                      :append-icon="show ? 'visibility_off' : 'visibility'"
                      outlined
                      color="blue darken-1"
                      name="password"
                      label="Password"
                      :type="show ? 'text' : 'password'"
                      ref="password"
                      v-model="form.password"
                      required
                      @click:append="show = !show"
                    ></v-text-field>
                  </v-col>
                  <v-col xs="12" text-xs-center>
                    <v-btn
                      color="primary"
                      block
                      dark
                      @click="submit"
                      :disabled="loading && !formIsValid"
                      :loading="loading"
                    >
                      {{ $t("login.connect") }}
                    </v-btn>
                  </v-col>
                </v-row>
              </form>
            </v-card-text>
            <v-card-text class="login-footer">
              <p class="text-center mb-0">
                Copyright &copy; {{ appName }} {{ appDate }}.
              </p>
            </v-card-text>
          </v-card>
        </v-col>
        <v-overlay absolute :value="sessionIsLoading">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-row>
    </v-layout>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { loadStates } from "../../utils";

export default {
  data() {
    return {
      drawer: null,
      checkbox: false,
      show: false,
      errorMsg: null,
      basiAuth: true,
      form: {
        username: "",
        password: "",
        userToken: ""
      },
      rules: {
        username: [value => !!value || "Username is required"],
        password: [value => !!value || "Password is required"],
        userToken: [value => !!value || "User Token is required"]
      },
      loading: false,
      appName: "PiTracker",
      appDate: new Date().getFullYear()
    };
  },

  computed: {
    ...mapGetters([
      "getSessionLoadState",
      "isAuth",
      "sessionIsInit",
      "getSessionToken"
    ]),
    formIsValid() {
      return this.basiAuth
        ? this.form.username !== "" && this.form.password !== ""
        : this.form.userToken !== "";
    },
    directLogin() {
      return !!this.$route.params.user_token;
    },

    binding() {
      const binding = {};

      if (this.$vuetify.breakpoint.mdAndUp) {
        binding.row = true;
      } else {
        binding.column = true;
      }

      return binding;
    },
    sessionIsLoading() {
      return this.getSessionLoadState === loadStates.PENDING;
    }
  },

  mounted() {
    document.title = `Connexion - ${this.appName}`;
    if (this.$route.params.user_token) {
      this.form.userToken = this.$route.params.user_token;
      this.loginWithToken();
    }
    if (this.isAuth && !this.sessionIsInit) {
      this.loading = true;
      this.reloadSession(this.getSessionToken).then(() => {
        this.loading = false;
        this.login();
      });
    }
  },

  methods: {
    ...mapActions(["initSession", "login", "loginUser", "reloadSession"]),

    resetForm() {
      Object.keys(this.form).forEach(f => this.$refs[f].reset());
    },

    submit() {
      this.loading = true;
      if (this.basiAuth) {
        this.basicLogin();
      } else {
        this.loginWithToken();
      }
    },
    loginWithToken() {
      this.initSession(this.form.userToken)
        .then(() => {
          this.loading = false;
          this.login();
          this.$router.push("/assets/index");
        })
        .catch(({ response }) => {
          this.loading = false;
          this.errorMsg = response.data[1];
          setTimeout(() => {
            this.errorMsg = null;
          }, 3000);
        });
    },
    basicLogin() {
      this.loginUser(this.form)
        .then(() => {
          this.loading = false;
          this.login();
          this.$router.push("/assets/index");
        })
        .catch(({ response }) => {
          this.loading = false;
          this.errorMsg = response.data[1];
          setTimeout(() => {
            this.errorMsg = null;
          }, 3000);
        });
    }
  }
};
</script>

<style>
#container {
  background: linear-gradient(
    180deg,
    #0072ce 0%,
    #00afd7 25%,
    #ececec 25%,
    #ececec 100%
  );
}

.login-footer {
  background: #f8f8f8;
  border-top: 1px solid #ececec;
}
</style>
